import type { FC } from 'react'
import { Chip, Stack } from '@mui/material'
import { Link } from 'react-router'
import { memberSurfacePath } from '~/entities/person'
import type { MemberIds } from '~/entities/person'
import type { LuciCitation } from './LuciService'

// A citation id opens `source:pk`; the source names the surface the way a timeline link's
// `object_type` does, so both route through the same map.
const CITATION_ID = /^([a-z_]+):(\d+)(?::|$)/

const citationPath = (id: string, member: MemberIds): string | null => {
  const [, source, pk] = CITATION_ID.exec(id) ?? []
  if (!source || !pk) return null
  return memberSurfacePath(source, Number(pk), member)
}

interface CitationChipsProps {
  citations: LuciCitation[]
  personId: number
  patientId: number | null
  onFollow: () => void
}

export const CitationChips: FC<CitationChipsProps> = ({
  citations,
  personId,
  patientId,
  onFollow,
}) => {
  if (citations.length === 0) return null
  return (
    <Stack
      direction="row"
      spacing={0.75}
      sx={{ mt: 1, flexWrap: 'wrap', rowGap: 0.75 }}
      data-testid="luci-citations"
    >
      {citations.map(citation => (
        <CitationChip
          key={citation.id}
          citation={citation}
          path={citationPath(citation.id, { patientId, personId })}
          onFollow={onFollow}
        />
      ))}
    </Stack>
  )
}

interface CitationChipProps {
  citation: LuciCitation
  path: string | null
  onFollow: () => void
}

const CitationChip: FC<CitationChipProps> = ({ citation, path, onFollow }) => {
  const testId = `luci-citation-${citation.id}`
  if (path == null) {
    return <Chip size="small" variant="outlined" label={citation.label} data-testid={testId} />
  }
  return (
    <Chip
      size="small"
      variant="outlined"
      clickable
      component={Link}
      to={path}
      onClick={onFollow}
      label={citation.label}
      data-testid={testId}
    />
  )
}
